import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, Newspaper, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const relativeTime = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    const d = Math.floor(diff / 86400);
    return d === 1 ? "yesterday" : `${d}d ago`;
  } catch {
    return "";
  }
};

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/news?limit=30");
      setNews(r.data);
    } catch (e) {
      setErr("Couldn't load the news feed. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="news-page" className="container-x py-16 md:py-24">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="eyebrow">Insights · Updated Daily</span>
          <h1 className="mt-4 h-display text-4xl sm:text-5xl lg:text-6xl">
            Indian banking, NBFCs &amp;<br />
            credit for businesses.
          </h1>
          <p className="mt-6 text-brand-sage text-lg leading-relaxed">
            A daily-refreshed digest of stories that move loan eligibility,
            interest rates and working-capital products — for manufacturers,
            distributors, MSMEs and salaried borrowers. Every item carries a
            quick AI-written gist below the headline.
          </p>
        </div>
        <button
          data-testid="news-refresh-btn"
          onClick={load}
          className="btn-ghost"
          aria-label="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="py-20 grid place-items-center text-brand-sage">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
        ) : err ? (
          <div data-testid="news-error" className="card-soft p-8 text-brand-forest">
            {err}
          </div>
        ) : news.length === 0 ? (
          <div data-testid="news-empty" className="card-soft p-10 text-center">
            <Newspaper className="mx-auto h-8 w-8 text-brand-sage" />
            <p className="mt-4 text-brand-sage">No articles yet — please check back shortly.</p>
          </div>
        ) : (
          <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {news.map((n, i) => (
              <li key={n.id}>
                <a
                  data-testid={`news-card-${i}`}
                  href={n.link}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-full flex-col card-soft p-6 transition-all hover:-translate-y-1 hover:border-brand-terracotta/40 hover:shadow-[0_28px_60px_-30px_rgba(17,42,34,0.3)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="rounded-full border border-brand-sand bg-brand-sand/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-forest/70">
                      {n.source}
                    </span>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-brand-terracotta opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </div>

                  <h3 className="mt-4 font-outfit font-bold text-lg text-brand-forest leading-snug group-hover:text-brand-terracotta transition-colors">
                    {n.title}
                  </h3>

                  {n.summary && (
                    <div className="mt-4 rounded-xl bg-brand-sand/40 border border-brand-sand p-4">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-terracotta">
                        <Sparkles className="h-3.5 w-3.5" /> AI Gist
                      </div>
                      <p className="mt-2 text-sm text-brand-forest/85 leading-relaxed">
                        {n.summary}
                      </p>
                    </div>
                  )}

                  <div className="mt-auto pt-5 flex items-center justify-between text-xs text-brand-sage">
                    <span title={fmtDate(n.published_at)}>{relativeTime(n.published_at)}</span>
                    <span className="font-medium text-brand-forest/60 group-hover:text-brand-terracotta transition-colors">
                      Read source →
                    </span>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-10 text-xs text-brand-sage max-w-3xl">
        News content is sourced from public RSS feeds of leading Indian
        publications. Summaries are AI-generated for quick scanning — for
        accuracy and full context please read the source article via the
        provided link. Swift Financial Solutions is not the publisher.
      </p>

      <div className="mt-12 flex justify-center">
        <Link to="/apply" data-testid="news-apply-cta" className="btn-primary">
          Talk to an advisor about your loan
        </Link>
      </div>
    </div>
  );
}
